<template>
    <div class="main-container">
        <v-container fluid class="edit-panel overflow-auto pa-5">
            <h4 class="mb-2">{{ $t('create_schedule') }}</h4>

            <v-form v-model="singleFormValid">
                <v-row dense>
                    <v-col cols="6">
                        <LabelField :label="$t('start_date')">
                            <DateTextfield
                                :value="scheduleToEdit.start_date"
                                :placeholder="$t('select_date')"
                                hide-details="auto"
                                @input="onDateInput('start_date', $event)"
                            />
                        </LabelField>
                    </v-col>
                    <v-col cols="6">
                        <LabelField :label="$t('start')">
                            <TimeTextfield
                                :value="scheduleToEdit.event_time_start"
                                :placeholder="$t('time_placeholder')"
                                hide-details="auto"
                                @input="onInput('event_time_start', $event)"
                            />
                        </LabelField>
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="6" />
                    <v-col cols="6">
                        <LabelField :label="$t('end')">
                            <TimeTextfield
                                :value="scheduleToEdit.event_time_end"
                                :placeholder="$t('time_placeholder')"
                                hide-details="auto"
                                @input="onInput('event_time_end', $event)"
                            />
                        </LabelField>
                    </v-col>
                </v-row>
                <v-row v-if="showDoorOpeningClosingHoursFields" dense>
                    <v-col cols="6">
                        <LabelField :label="$t('opening_time_start')">
                            <TimeTextfield
                                :value="scheduleToEdit.opening_time_start"
                                :placeholder="$t('time_placeholder')"
                                hide-details="auto"
                                @input="onInput('opening_time_start', $event)"
                            />
                        </LabelField>
                    </v-col>
                    <v-col cols="6">
                        <LabelField :label="$t('opening_time_end')">
                            <TimeTextfield
                                :value="scheduleToEdit.opening_time_end"
                                :placeholder="$t('time_placeholder')"
                                hide-details="auto"
                                @input="onInput('opening_time_end', $event)"
                            />
                        </LabelField>
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12">
                        <LabelField
                            :label="$t('interessen_tags_erweiterte_suche')"
                            :label-tip="$t('interessen_tags_erweiterte_suche_tip')"
                        >
                            <TagSelect
                                :value="scheduleToEdit.tags"
                                item-value="tag"
                                clearable
                                for-schedule
                                hide-details="auto"
                                @input="onInput('tags', $event)"
                            />
                        </LabelField>
                    </v-col>
                </v-row>
                <v-row dense>
                    <v-col cols="12" align="end">
                        <ActionBtn :disabled="!readyToSave" :loading="saving" @click="saveSchedule">
                            <KpIcon icon-cls="calendar" color="white" disabled-color="grey" />
                            {{ $t('save_schedule') }}
                        </ActionBtn>
                    </v-col>
                </v-row>
            </v-form>
        </v-container>

        <div class="info-panel overflow-auto pa-5">
            <h4 class="mb-2">{{ $t('deine_erfassten_termine') }}</h4>
            <ScheduleCardList
                ref="scheduleCardList"
                :event="record"
                @edit="editSchedule"
                @deleted="afterScheduleDelete"
            />
        </div>
    </div>
</template>

<script>
import dayjs from 'dayjs';
import { errToStr } from '@/helpers/tools';
import { Event } from '@/models/Event';
import { Schedule } from '@/models/Schedule';
import ScheduleCardList from '@/components/event/ScheduleCardList';
import TagSelect from '@/components/tag/select';

export default {
    name: 'EventEditExpressSchedule',
    components: {
        ScheduleCardList,
        TagSelect,
    },

    props: {
        record: {
            type: Event,
            required: true,
        },
    },

    data() {
        return {
            singleFormValid: false,
            scheduleToEdit: this.createEmptySingleSchedule(),
            saving: false,
        };
    },

    computed: {
        readyToSave() {
            return !!(this.scheduleToEdit?.start_date?.isValid() && this.scheduleToEdit?.event_time_start);
        },
        showDoorOpeningClosingHoursFields() {
            // refs #25821
            if (
                this.record.event_class === Event.CLASS_EVENT ||
                (this.record.event_class === Event.CLASS_EXHIBITION && this.scheduleToEdit.isRecurring)
            ) {
                return true;
            }

            return false;
        },
    },

    watch: {
        record: {
            handler(newValue, oldValue) {
                if (newValue?.getId() !== oldValue?.getId()) {
                    this.$refs.scheduleCardList.loadSchedules();
                }
            },
            deep: true,
        },
    },

    async mounted() {
        this.$emit('input', true);
    },

    methods: {
        createEmptySingleSchedule() {
            return new Schedule({
                event_id: this.record.getId(),
                recurring_type: Schedule.RECURRING_TYPE_SINGLE,
                recur_every_n: null,
            });
        },
        onInput(field, value) {
            this.scheduleToEdit.set(field, value);
        },

        onDateInput(field, value) {
            if (typeof value === 'string') {
                this.scheduleToEdit.set(field, dayjs(value));
            } else {
                this.scheduleToEdit.set(field, value);
            }
        },

        async afterScheduleDelete(schedule) {
            this.scheduleToEdit = this.createEmptySingleSchedule();
            this.$emit('reload-event');
        },

        editSchedule(schedule) {
            if (schedule.recurring_type === Schedule.RECURRING_TYPE_SINGLE) {
                this.scheduleToEdit = schedule;
            } else {
                this.$toast({
                    message: 'Diese Einträge können nur über die erweiterte Maske bearbeitet werden.',
                    color: 'error',
                });
            }
        },

        /**
         * called from the parent component before the global save/navigation occurs.
         * here we save the actual schedule.
         */
        async beforeSave() {
            if (this.readyToSave) {
                await this.saveSchedule();
            }
        },

        async saveSchedule() {
            if (this.scheduleToEdit) {
                try {
                    this.saving = true;
                    if (this.scheduleToEdit.isDirty()) {
                        await this.scheduleToEdit.save();
                    }
                    this.scheduleToEdit = this.createEmptySingleSchedule();
                    this.$refs.scheduleCardList?.loadSchedules();
                    this.$emit('reload-event'); // also loads schedules
                } catch (e) {
                    this.$toast({ message: errToStr(e), color: 'error' });
                } finally {
                    this.saving = false;
                }
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.main-container {
    display: grid;
    grid-template-areas: 'edit info';
    grid-template-rows: auto;
    grid-template-columns: minmax(300px, 600px) minmax(440px, 1fr);
}
.edit-panel {
    grid-area: edit;
}
.info-panel {
    grid-area: info;
    background-color: var(--toolbar-bg-color);
}
</style>
